import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";

const DemoBrandScanView: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useAppContext();
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);

  const handleBack = () => {
    navigate("/demobrand/before-start");
  };

  const handleScan = () => {
    setIsScanning(true);
    setScanProgress(0);
  };

  // Simulate scanning progress
  useEffect(() => {
    if (isScanning && scanProgress < 100) {
      const timer = setTimeout(() => {
        setScanProgress((prev) => Math.min(prev + 10, 100));
      }, 200);
      return () => clearTimeout(timer);
    } else if (scanProgress >= 100) {
      // Simulate detected item - use PREVIEW_PRODUCT to set lastAddedProduct
      dispatch({
        type: "PREVIEW_PRODUCT",
        payload: {
          id: `demo-${Date.now()}`,
          name: "Demo Brand Performance Jacket",
          brand: "Demo Brand",
          image: "/assets/images/demobrand/jacket.jpg",
          size: "M",
          price: 149,
          rewardValue: 45,
          isBranded: true,
        },
      });
      setTimeout(() => {
        navigate("/demobrand/offer");
      }, 500);
    }
  }, [isScanning, scanProgress, dispatch, navigate]);

  return (
    <div className="min-h-screen bg-[#0A1628] flex flex-col">
      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-between">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-white font-semibold text-xl">Demo Brand</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-4">
            {isScanning ? "Scanning..." : "Place Your Item"}
          </h1>
          <p className="text-xl text-white/60 max-w-lg mx-auto">
            {isScanning
              ? "Our AI is analyzing your item"
              : "Position your item on the scanning pad and tap Scan"}
          </p>
        </motion.div>

        {/* Scan Area */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="w-80 h-80 bg-white/5 rounded-2xl border-2 border-dashed border-white/20 flex items-center justify-center mb-8 relative overflow-hidden"
        >
          {isScanning ? (
            <>
              {/* Scanning animation */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-b from-[#0066CC]/30 to-transparent"
                initial={{ y: "-100%" }}
                animate={{ y: "100%" }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              />
              <div className="text-center z-10">
                <div className="text-[#0066CC] text-6xl mb-4">
                  <svg
                    className="w-16 h-16 mx-auto animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-white/80 font-medium">
                  {scanProgress}% Complete
                </p>
              </div>
            </>
          ) : (
            <div className="text-center">
              <svg
                className="w-20 h-20 text-white/30 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <p className="text-white/50">Place item here</p>
            </div>
          )}
        </motion.div>

        {/* Progress bar when scanning */}
        {isScanning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-80 bg-white/10 rounded-full h-2 mb-8"
          >
            <motion.div
              className="bg-[#0066CC] h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${scanProgress}%` }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>
        )}

        {/* CTA Button */}
        {!isScanning && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            onClick={handleScan}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-[#0066CC] text-white px-12 py-4 rounded-lg font-bold tracking-wider text-lg shadow-xl hover:bg-[#0055AA] transition-colors"
          >
            Scan Item
          </motion.button>
        )}
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default DemoBrandScanView;
